import { useEffect, useRef } from 'react';

import { useDataSeriesQuery } from '@/features/time-series-chart/api/data-series/useDataSeriesQuery';
import type { TimeWindowMicros } from '@/features/time-series-chart/types/TimeWindow.types';

import { SLOW_RENDER_MS } from '../config/slownessConfig';
import type { DiagramSlowness } from '../types/DiagramSlowness.types';
import { afterNextPaint } from '../utils/afterNextPaint';

type UseSlowRenderDetectionParams = {
  targetId: number | undefined;
  timeWindow: TimeWindowMicros | undefined;
  requestedSeriesIds: string[];
  enabled: boolean;
  onSlowRender: (slowness: Extract<DiagramSlowness, { kind: 'render' }>) => void;
};

/**
 * Times every fresh response from its arrival until the chart drawn from it is on screen. It
 * observes the request the chart itself makes; the query cache shares it, so nothing is fetched twice.
 */
export function useSlowRenderDetection({
  targetId,
  timeWindow,
  requestedSeriesIds,
  enabled,
  onSlowRender,
}: UseSlowRenderDetectionParams) {
  const { isFetching, dataUpdatedAt } = useDataSeriesQuery({
    targetId,
    timeWindow,
    selectedSeriesIds: requestedSeriesIds,
  });
  const fetchStartedAtRef = useRef<number | undefined>(undefined);

  // Declared before the fetch tracking below, so it still sees the start of the fetch that just ended.
  useEffect(() => {
    const fetchStartedAt = fetchStartedAtRef.current;

    // Data older than the fetch came from the cache: nothing new was drawn.
    if (!enabled || fetchStartedAt === undefined || dataUpdatedAt < fetchStartedAt || document.hidden) {
      return undefined;
    }

    // A hidden tab gets no frames, so the wait would last until the user comes back.
    let wasHidden = false;
    const markHidden = () => {
      wasHidden ||= document.hidden;
    };
    const cancelPaintWait = afterNextPaint(() => {
      const renderMs = Date.now() - dataUpdatedAt;

      if (renderMs > SLOW_RENDER_MS && !wasHidden) {
        onSlowRender({ kind: 'render', renderMs, networkMs: dataUpdatedAt - fetchStartedAt });
      }
    });

    document.addEventListener('visibilitychange', markHidden);

    return () => {
      cancelPaintWait();
      document.removeEventListener('visibilitychange', markHidden);
    };
  }, [dataUpdatedAt, enabled, onSlowRender]);

  useEffect(() => {
    fetchStartedAtRef.current = isFetching ? (fetchStartedAtRef.current ?? Date.now()) : undefined;
  }, [isFetching]);
}
